import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  List,
  ListItem,
  Link,
  Divider,
  Fade,
} from '@mui/material';
import { CloudUpload as UploadIcon, GetApp as DownloadIcon } from '@mui/icons-material';
import AuthService from '../services/AuthService';

/**
 * CSV Parser Component
 * Processes CSV files from various sources (Amazon, eBay, PayPal, Shopify, JTL)
 * Reformats customer numbers to ensure 7-digit format
 */
const CsvParser: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [parsedFiles, setParsedFiles] = useState<string[]>([]);
  const [parseSuccess, setParseSuccess] = useState(false);
  const [tabIndex, setTabIndex] = useState(2); // Store tab index to restore after refresh
  
  // Reference to file input for resetting
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch file list on component mount and after operations
  useEffect(() => {
    fetchFiles();
  }, []);

  // Show success message effect
  useEffect(() => {
    if (parseSuccess) {
      // Save tab index in local storage before refresh
      localStorage.setItem('activeTabIndex', tabIndex.toString());
      localStorage.setItem('showParseSuccess', 'true');
      
      // Refresh the page after a short delay
      const timer = setTimeout(() => {
        window.location.reload();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [parseSuccess, tabIndex]);

  // Check for success message after page reload
  useEffect(() => {
    const savedTabIndex = localStorage.getItem('activeTabIndex');
    const showSuccess = localStorage.getItem('showParseSuccess');
    
    if (showSuccess === 'true') {
      setSuccess('Erfolgreich geparst und gespeichert');
      
      // Clear the flag
      localStorage.removeItem('showParseSuccess');
      
      // Auto-hide the success message after 5 seconds
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const fetchFiles = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/jtl-helper/csv/files`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
      });

      if (!response.ok) {
        // Check if token expired (403 error)
        if (response.status === 403) {
          setError("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
          AuthService.logout();
          window.location.reload(); // Reload to show login page
          return;
        }
        throw new Error(`Failed to fetch files (Status: ${response.status})`);
      }

      const data = await response.json();
      
      // Split files into uploaded and parsed lists
      const uploaded = data.files.filter((file: string) => !file.includes('_parsed'));
      const parsed = data.files.filter((file: string) => file.includes('_parsed'));
      
      setUploadedFiles(uploaded);
      setParsedFiles(parsed);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError((err as Error).message);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    
    // Append all selected files to the FormData
    for (let i = 0; i < files.length; i++) {
      formData.append("csv", files[i]);
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setProcessingStatus("Dateien werden hochgeladen...");

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/jtl-helper/csv/upload`, {
        method: "POST",
        body: formData,
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
      });

      resetFileInput();

      if (!response.ok) {
        // Check if token expired (403 error)
        if (response.status === 403) {
          setError("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
          AuthService.logout();
          window.location.reload(); // Reload to show login page
          return;
        }
        throw new Error(`Fehler beim Hochladen der Datei(en) (Status: ${response.status})`);
      }

      const result = await response.json();
      setSuccess("Dateien erfolgreich hochgeladen");
      fetchFiles(); // Refresh file list
    } catch (err) {
      console.error('Error in file upload:', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
      setProcessingStatus("");
    }
  };

  const processFiles = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setProcessingStatus("Dateien werden verarbeitet...");

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/jtl-helper/csv/process`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
      });

      if (!response.ok) {
        // Check if token expired (403 error)
        if (response.status === 403) {
          setError("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
          AuthService.logout();
          window.location.reload(); // Reload to show login page
          return;
        }
        throw new Error(`Fehler beim Verarbeiten der Dateien (Status: ${response.status})`);
      }

      const result = await response.json();
      setSuccess(`${result.processed} Dateien erfolgreich verarbeitet`);
      fetchFiles(); // Refresh file list
    } catch (err) {
      console.error('Error processing files:', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
      setProcessingStatus("");
    }
  };

  // Function to delete a file from the server
  const deleteFile = async (filename: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/jtl-helper/csv/delete/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          setError("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
          AuthService.logout();
          window.location.reload();
          return;
        }
        throw new Error(`Fehler beim Löschen der Datei (Status: ${response.status})`);
      }
    } catch (err) {
      console.error('Error deleting file:', err);
      // Don't show error to user, just log it
    }
  };

  // Function to delete all files in a list
  const deleteAllFiles = async (filenames: string[]) => {
    await Promise.all(filenames.map(filename => deleteFile(filename)));
  };

  const downloadFile = async (filename: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/jtl-helper/csv/download/${encodeURIComponent(filename)}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
      });

      if (!response.ok) {
        // Check if token expired (403 error)
        if (response.status === 403) {
          setError("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
          AuthService.logout();
          window.location.reload(); // Reload to show login page
          return;
        }
        throw new Error(`Fehler beim Herunterladen der Datei (Status: ${response.status})`);
      }

      // Create a blob from the response
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Create a download link and trigger it
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Get the base filename to find related files to delete
      const baseFilename = filename.replace('_parsed.csv', '');
      
      // Find the original file based on the related base name
      const originalFile = uploadedFiles.find(f => f.includes(baseFilename));
      
      // Delete both the original and parsed files
      if (originalFile) {
        await deleteFile(originalFile);
      }
      await deleteFile(filename);
      
      // Set success flag to trigger page refresh
      setParseSuccess(true);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError((err as Error).message);
    }
  };

  // Function to reset the file input
  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        CSV Parser
      </Typography>
      
      <Typography variant="body1" paragraph>
        Die hochgeladenen CSV Dateien werden geparst und alle Kundennummern,
        welche &gt; 1000000 (7 stellen) werden belassen, die anderen KD Nummern
        werden dagegen mit einem 00-Suffix geändert, damit alle KD Nummern 7-Stellen haben.
      </Typography>
      
      <Typography variant="body2" paragraph>
        Hochgeladen werden muss:
        <ul>
          <li>Amazon: Alle CSVs</li>
          <li>eBay: Alle CSVs</li>
          <li>Paypal: Alle CSVs</li>
          <li>Shopify: Alle CSVs</li>
          <li>Verkauf: Nur "EXTF_Stamm_DebKred_xxxx.csv"</li>
        </ul>
      </Typography>
      
      {processingStatus && (
        <Box mb={2} display="flex" alignItems="center" gap={2}>
          <CircularProgress size={20} />
          <Typography>{processingStatus}</Typography>
        </Box>
      )}

      <Box mb={4} display="flex" flexDirection="column" gap={2}>
        <Box display="flex" gap={2} alignItems="center">
          <input 
            ref={fileInputRef}
            accept=".csv" 
            style={{ display: "none" }} 
            id="csv-file-upload" 
            type="file" 
            multiple
            onChange={handleFileUpload} 
            disabled={loading} 
          />
          <label htmlFor="csv-file-upload">
            <Button 
              variant="contained" 
              component="span" 
              disabled={loading}
              startIcon={<UploadIcon />}
            >
              1. CSV(s) hochladen
            </Button>
          </label>
          
          <Button 
            variant="contained" 
            color="primary" 
            onClick={processFiles}
            disabled={loading || uploadedFiles.length === 0}
          >
            2. Verarbeiten
          </Button>
        </Box>
      </Box>
      
      {/* Uploaded Files Section */}
      {uploadedFiles.length > 0 && (
        <Box mt={3} mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Hochgeladene Dateien:
          </Typography>
          
          <List>
            {uploadedFiles.map((file, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <Typography variant="body2">{file}</Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
      
      <Divider sx={{ my: 2 }} />
      
      {/* Processed Files Section */}
      {parsedFiles.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle1" gutterBottom>
            3. Verarbeitete Dateien herunterladen:
          </Typography>
          
          <List>
            {parsedFiles.map((file, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <Link 
                  component="button" 
                  variant="body2" 
                  onClick={() => downloadFile(file)}
                >
                  {file}
                </Link>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert onClose={() => setSuccess(null)} severity="success">
          <Fade in={!!success}>
            <Typography>{success}</Typography>
          </Fade>
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default CsvParser;